// src/components/ui/avatar/Avatar.tsx
'use client';

import style from './Avatar.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

type BorderLevel = 0 | 1 | 2 | 3 | 4 | 5 | boolean;

type AvatarProps = {
  src?: string | null;
  href?: string;

  /** XS/基準尺寸（<576px） */
  size?: number;
  /** 斷點覆寫尺寸（選填） */
  sizeSm?: number; // >= 576px
  sizeMd?: number; // >= 768px
  sizeLg?: number; // >= 992px
  sizeXl?: number; // >= 1200px
  sizeXxl?: number; // >= 1400px

  /** 圖片替代文字，預設 "頭像" */
  alt?: string;

  /**
   * 邊框粗細（0~5；true 視為 1）。例如 border={2} -> "border border-2"
   * 也支援斷點覆寫：borderSm/borderMd/borderLg/borderXl/borderXxl
   */
  border?: BorderLevel;
  borderSm?: BorderLevel;
  borderMd?: BorderLevel;
  borderLg?: BorderLevel;
  borderXl?: BorderLevel;
  borderXxl?: BorderLevel;

  /**
   * 邊框顏色（對應 Bootstrap，如 'white' | 'primary' | 'secondary' | ...）
   * e.g. borderColor="white" -> class "border-white"
   */
  borderColor?: string;

  /** 額外客製 className */
  className?: string;
};

const BOOTSTRAP_BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

// 正規化 border 值：boolean -> 1/0；數字夾在 0~5 之間；undefined -> undefined（代表未覆寫）
function normalizeBorder(v: BorderLevel | undefined): number | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'boolean') return v ? 1 : 0;
  // 夾在 0~5
  return Math.max(0, Math.min(5, v));
}

const Avatar = ({
  src,
  href,
  size = 60,
  sizeSm,
  sizeMd,
  sizeLg,
  sizeXl,
  sizeXxl,
  alt = '頭像',
  border,
  borderSm,
  borderMd,
  borderLg,
  borderXl,
  borderXxl,
  borderColor,
  className,
}: AvatarProps) => {
  // 實際尺寸
  const [effectiveSize, setEffectiveSize] = useState<number>(size);
  // 實際邊框等級（0~5）
  const [effectiveBorder, setEffectiveBorder] = useState<number>(normalizeBorder(border) ?? 0);
  const hasImage = typeof src === 'string' && src.trim() !== '';

  useEffect(() => {
    const computeSize = (w: number) => {
      if (w >= BOOTSTRAP_BREAKPOINTS.xxl && sizeXxl != null) return sizeXxl;
      if (w >= BOOTSTRAP_BREAKPOINTS.xl && sizeXl != null) return sizeXl;
      if (w >= BOOTSTRAP_BREAKPOINTS.lg && sizeLg != null) return sizeLg;
      if (w >= BOOTSTRAP_BREAKPOINTS.md && sizeMd != null) return sizeMd;
      if (w >= BOOTSTRAP_BREAKPOINTS.sm && sizeSm != null) return sizeSm;
      return size;
    };

    const computeBorder = (w: number) => {
      const bXxl = normalizeBorder(borderXxl);
      const bXl = normalizeBorder(borderXl);
      const bLg = normalizeBorder(borderLg);
      const bMd = normalizeBorder(borderMd);
      const bSm = normalizeBorder(borderSm);
      const bBase = normalizeBorder(border) ?? 0;

      if (w >= BOOTSTRAP_BREAKPOINTS.xxl && bXxl != undefined) return bXxl;
      if (w >= BOOTSTRAP_BREAKPOINTS.xl && bXl != undefined) return bXl;
      if (w >= BOOTSTRAP_BREAKPOINTS.lg && bLg != undefined) return bLg;
      if (w >= BOOTSTRAP_BREAKPOINTS.md && bMd != undefined) return bMd;
      if (w >= BOOTSTRAP_BREAKPOINTS.sm && bSm != undefined) return bSm;
      return bBase;
    };

    const apply = () => {
      const w = window.innerWidth;
      setEffectiveSize(computeSize(w));
      setEffectiveBorder(computeBorder(w));
    };

    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [
    size,
    sizeSm,
    sizeMd,
    sizeLg,
    sizeXl,
    sizeXxl,
    border,
    borderSm,
    borderMd,
    borderLg,
    borderXl,
    borderXxl,
  ]);

  // 組合 class
  const borderClasses = [
    effectiveBorder > 0 ? 'border' : '',
    effectiveBorder > 0 ? `border-${effectiveBorder}` : '',
    borderColor ? `border-${borderColor}` : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const avatar = (
    <div
      className={`${style.avatarWrapper} ${borderClasses}`}
      style={{ width: `${effectiveSize}px`, height: `${effectiveSize}px` }}
    >
      {hasImage ? (
        <Image
          src={src as string}
          alt={alt}
          fill
          className={style.avatarImage}
          sizes={`${effectiveSize}px`}
        />
      ) : (
        <div className={style.avatarFallback}>{/* 這裡可以放預設 icon / 字母，暫時留空也可 */}</div>
      )}
    </div>
  );

  return href ? <Link href={href}>{avatar}</Link> : avatar;
};

export default Avatar;
